import { Routes } from '@angular/router';

import { authGuard, authMatchGuard } from './core/guards/auth.guard';
import { permissionGuard, permissionMatchGuard } from './core/guards/permission.guard';
import { roleGuard, roleMatchGuard } from './core/guards/role.guard';
import {
  verificadorPreciosActivoGuard,
  verificadorPreciosActivoMatchGuard,
} from './core/guards/verificador-precios.guard';
import { MenuPermissions } from './core/constants/menu-permissions';
import { ModuleIds } from './core/constants/module-ids';
import { EstadisticaPermissions } from './core/constants/estadistica-permissions';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { ViewsLayoutComponent } from './layouts/views-layout/views-layout.component';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: '404',
    loadComponent: () => import('./shared/pages/not-found.page').then((m) => m.NotFoundPage),
  },
  {
    path: 'forbidden',
    loadComponent: () => import('./shared/pages/forbidden.page').then((m) => m.ForbiddenPage),
  },
  {
    // Kiosco público del verificador de precios: fuera de `MainLayoutComponent`
    // y sin `authGuard`/`permissionGuard` (es de cara al cliente, sin sesión).
    // Su única condición de acceso es que la configuración lo tenga activo.
    path: 'verificador-precios',
    canActivate: [verificadorPreciosActivoGuard],
    canMatch: [verificadorPreciosActivoMatchGuard],
    loadComponent: () =>
      import('./features/verificador-precios/verificador-precios.page').then(
        (m) => m.VerificadorPreciosPage,
      ),
  },
  {
    // Pantallas de planta con layout alternativo (sin sidebar ni topbar). Piden sesión, no permiso de
    // ruta (criterio legacy): cada pantalla resuelve lo que el usuario puede hacer con sus permisos.
    path: 'views',
    component: ViewsLayoutComponent,
    canActivate: [authGuard],
    canMatch: [authMatchGuard],
    children: [
      {
        path: 'carga-operativa',
        loadChildren: () =>
          import('./features/produccion/carga-operativa/carga-operativa.routes').then(
            (m) => m.CARGA_OPERATIVA_ROUTES,
          ),
      },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      {
        path: 'dashboard',
        canActivate: [authGuard],
        canMatch: [authMatchGuard],
        loadComponent: () =>
          import('./features/dashboard/dashboard-shell.component').then(
            (m) => m.DashboardShellComponent,
          ),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'overview',
          },
          {
            path: 'overview',
            loadComponent: () =>
              import('./features/dashboard/dashboard.page').then((m) => m.DashboardPage),
          },
          {
            path: 'activity',
            loadComponent: () =>
              import('./features/dashboard/dashboard-activity.page').then(
                (m) => m.DashboardActivityPage,
              ),
          },
        ],
      },
      {
        path: 'perfil',
        canActivate: [authGuard],
        canMatch: [authMatchGuard],
        loadComponent: () => import('./features/perfil/perfil.page').then((m) => m.PerfilPage),
      },
      {
        path: 'configuracion',
        canActivate: [authGuard],
        canMatch: [authMatchGuard],
        loadComponent: () =>
          import('./features/configuracion/configuracion.page').then((m) => m.ConfiguracionPage),
      },
      {
        path: 'info/novedades',
        canActivate: [authGuard],
        canMatch: [authMatchGuard],
        loadComponent: () =>
          import('./modules/info/pages/novedades/novedades.component').then(
            (m) => m.NovedadesComponent,
          ),
      },
      {
        path: 'dashboards',
        canActivate: [authGuard],
        canMatch: [authMatchGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'articulos',
          },
          {
            path: 'articulos',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.ARTICULOS],
            },
            loadComponent: () =>
              import('./features/dashboard/articulos/articulos-listado.page').then(
                (m) => m.ArticulosListadoPage,
              ),
          },
          {
            path: 'articulos/consulta-precio',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.ARTICULOS],
            },
            loadComponent: () =>
              import('./features/dashboard/articulos/consulta-precio/consulta-precio.page').then(
                (m) => m.ConsultaPrecioPage,
              ),
          },
          {
            path: 'articulos/consulta-stock',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.ARTICULOS],
            },
            loadComponent: () =>
              import('./features/dashboard/articulos/consulta-stock/consulta-stock.page').then(
                (m) => m.ConsultaStockPage,
              ),
          },
          {
            path: 'atributos',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.ATRIBUTOS],
            },
            loadComponent: () =>
              import('./features/dashboard/atributos/atributos-listado.page').then(
                (m) => m.AtributosListadoPage,
              ),
          },
        ],
      },
      {
        path: 'notificaciones',
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'pendientes',
          },
          {
            path: 'listadoNotificaciones',
            redirectTo: 'pendientes',
          },
          {
            path: 'pendientes',
            data: { mode: 'pendientes' },
            loadComponent: () =>
              import('./features/notificaciones/listado-notificaciones/notificaciones-listado.page').then(
                (m) => m.NotificacionesListadoPage,
              ),
          },
          {
            path: 'historico',
            data: { mode: 'historico' },
            loadComponent: () =>
              import('./features/notificaciones/listado-notificaciones/notificaciones-listado.page').then(
                (m) => m.NotificacionesListadoPage,
              ),
          },
          {
            path: 'configuracionNotificacion',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, roleMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.CONFIGURACION_NOTIFICACIONES],
            },
            loadComponent: () =>
              import('./features/notificaciones/configuracion-notificacion/configuracion-notificacion.page').then(
                (m) => m.ConfiguracionNotificacionPage,
              ),
          },
          {
            path: 'gestionar-configs-receptor',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, roleMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.CONFIGURACION_NOTIFICACIONES],
            },
            loadComponent: () =>
              import('./features/notificaciones/gestionar-configs-receptor/gestionar-configs-receptor.page').then(
                (m) => m.GestionarConfigsReceptorPage,
              ),
          },
        ],
      },
      {
        path: 'estadistica',
        canActivate: [authGuard, permissionGuard],
        canMatch: [authMatchGuard, permissionMatchGuard],
        data: {
          moduleId: ModuleIds.ESTADISTICA,
          permissions: [],
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'tableros',
          },
          {
            path: 'tableros',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [EstadisticaPermissions.COLECCION_LISTAR],
            },
            loadComponent: () =>
              import('./features/estadistica/tableros/estadistica-tableros.page').then(
                (m) => m.EstadisticaTablerosPage,
              ),
          },
          {
            path: 'indicadores',
            data: {
              permissions: [1158], // PERMISO_LIS_INDICADORES
            },
            loadComponent: () =>
              import('./features/estadistica/indicadores/estadistica-indicadores.page').then(
                (m) => m.EstadisticaIndicadoresPage,
              ),
          },
          {
            path: 'indicador/:idInstancia/:idIndicadorRel/:idTablero',
            data: {
              permissions: [EstadisticaPermissions.COLECCION_LISTAR],
            },
            loadComponent: () =>
              import('./features/estadistica/tableros/components/ver-indicador-instancia/ver-indicador-instancia.page').then(
                (m) => m.VerIndicadorInstanciaPage,
              ),
          },
        ],
      },
      {
        path: 'integraciones',
        canActivate: [authGuard, permissionGuard],
        canMatch: [authMatchGuard, permissionMatchGuard],
        data: {
          moduleId: ModuleIds.WEB,
          roles: ['admin', 'integraciones'],
          permissions: [],
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/integraciones/integraciones.page').then(
                (m) => m.IntegracionesPage,
              ),
          },
          {
            path: 'pagos',
            children: [
              {
                path: 'cajas',
                loadComponent: () =>
                  import('./features/integraciones/pagos/cajas/listado-cajas/listado-cajas.page').then(
                    (m) => m.ListadoCajasPage,
                  ),
              },
              {
                path: 'sucursales',
                loadComponent: () =>
                  import('./features/integraciones/pagos/sucursales/listado-sucursales/listado-sucursales.page').then(
                    (m) => m.ListadoSucursalesPage,
                  ),
              },
              {
                path: 'usuarios',
                loadComponent: () =>
                  import('./features/integraciones/pagos/usuarios/listado-usuarios/listado-usuarios.page').then(
                    (m) => m.ListadoUsuariosPage,
                  ),
              },
              {
                path: 'verificar',
                loadComponent: () =>
                  import('./features/integraciones/pagos/verificar/verificar.page').then(
                    (m) => m.VerificarPagosPage,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'produccion',
        canActivate: [authGuard, permissionGuard],
        canMatch: [authMatchGuard, permissionMatchGuard],
        data: {
          moduleId: ModuleIds.PRODUCCION,
          roles: [],
          permissions: [],
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'tipos-maquina',
          },
          {
            path: 'tipos-maquina',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_TIPOS_MAQUINA],
            },
            loadComponent: () =>
              import('./features/produccion/tipos-maquina/tipos-maquina.page').then(
                (m) => m.TiposMaquinaPage,
              ),
          },
          {
            path: 'maquina',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_MAQUINAS],
            },
            loadComponent: () =>
              import('./features/produccion/maquinas/maquinas.page').then(
                (m) => m.MaquinasPage,
              ),
          },
          {
            path: 'operacion',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_OPERACIONES],
            },
            loadComponent: () =>
              import('./features/produccion/operaciones/operaciones.page').then(
                (m) => m.OperacionesPage,
              ),
          },
          {
            path: 'ruta-produccion',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_RUTAS_PRODUCCION],
            },
            loadComponent: () =>
              import('./features/produccion/rutas-produccion/rutas-produccion.page').then(
                (m) => m.RutasProduccionPage,
              ),
          },
          {
            path: 'ordenes-produccion',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_ORDENES_PRODUCCION],
            },
            loadComponent: () =>
              import(
                './features/produccion/ordenes-produccion/operativa/ordenes-produccion-operativa.page'
              ).then((m) => m.OrdenesProduccionOperativaPage),
          },
          {
            path: 'ordenes-produccion/listado',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_ORDENES_PRODUCCION_LISTADO],
            },
            loadComponent: () =>
              import(
                './features/produccion/ordenes-produccion/listado/ordenes-produccion-listado.page'
              ).then((m) => m.OrdenesProduccionListadoPage),
          },
          {
            path: 'articulos-produccion',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.PRD_ARTICULOS],
              // Reutiliza literalmente ArticulosListadoPage: fija el filtro EsProduccion
              // sin exponerlo en la UI (ver ArticulosListadoPage.isProduccionView).
              produccion: true,
            },
            loadComponent: () =>
              import('./features/dashboard/articulos/articulos-listado.page').then(
                (m) => m.ArticulosListadoPage,
              ),
          },
        ],
      },
      {
        path: 'rma',
        canActivate: [authGuard, permissionGuard],
        canMatch: [authMatchGuard, roleMatchGuard, permissionMatchGuard],
        data: {
          moduleId: ModuleIds.RMA,
          roles: [],
          permissions: [],
        },
        loadComponent: () => import('./features/rma/rma.page').then((m) => m.RmaPage),
      },
      {
        path: 'autorizacion',
        canActivate: [authGuard, permissionGuard],
        canMatch: [authMatchGuard, permissionMatchGuard],
        data: {
          moduleId: ModuleIds.AUTORIZACION,
          roles: [],
          permissions: [],
        },
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'autorizar-comprobantes',
          },
          {
            path: 'config-comprobantes',
            canActivate: [authGuard, permissionGuard],
            canMatch: [authMatchGuard, permissionMatchGuard],
            data: {
              permissions: [MenuPermissions.AUT_RESPONSABLES],
            },
            loadComponent: () =>
              import('./features/autorizacion/config-comprobantes-listado/config-comprobantes-listado.page').then(
                (m) => m.ConfigComprobantesListadoPage,
              ),
          },
           {
             path: 'autorizar-comprobantes',
             canActivate: [authGuard, permissionGuard],
             canMatch: [authMatchGuard, permissionMatchGuard],
             data: {
               permissions: [MenuPermissions.AUT_AUTORIZAR_COMPROBANTES],
             },
             children: [
               {
                 path: '',
                 pathMatch: 'full',
                 redirectTo: 'pendientes',
               },
               {
                 path: 'pendientes',
                 data: { modo: 'pendientes' },
                 loadComponent: () =>
                   import('./features/autorizacion/autorizar-comprobantes/autorizar-comprobantes-listado.page').then(
                     (m) => m.AutorizarComprobanteListadoPage,
                   ),
               },
               {
                 path: 'historico',
                 data: { modo: 'historico' },
                 loadComponent: () =>
                   import('./features/autorizacion/autorizar-comprobantes/autorizar-comprobantes-listado.page').then(
                     (m) => m.AutorizarComprobanteListadoPage,
                   ),
               },
             ],
           },
        ],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '404',
  },
];
